import { useEffect, useMemo, useState } from 'react'
import { ShieldCheck, CheckCircle2, CalendarDays, MapPin, Clock, Users, Loader2, AlertCircle } from 'lucide-react'

import { cn } from '@/core/lib/utils'
import { fmtDate } from '@/core/lib/formatters'
import { Button } from '@/ui/button'

import { fetchPublicCourses, fetchPublicSessions, createCheckoutSession } from '../api/publicSessionsService'
import type { CourseFromAPI } from '../stores/types'
import type { PublicSession } from '../api/publicSessionsService'

type Step = 0 | 1 | 2 | 3

interface Props {
  embedded?: boolean
}

/** Validate that checkout URL is from Stripe (prevent open redirect). */
function isValidCheckoutUrl(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'https:' && (
      u.hostname === 'checkout.stripe.com' ||
      u.hostname.endsWith('.stripe.com')
    )
  } catch {
    return false
  }
}

export function PublicBookClassPage({ embedded = false }: Props) {
  const [step, setStep] = useState<Step>(0)
  const [courses, setCourses] = useState<CourseFromAPI[]>([])
  const [sessions, setSessions] = useState<PublicSession[]>([])
  const [courseSlug, setCourseSlug] = useState<string>('')
  const [sessionId, setSessionId] = useState<string>('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [company, setCompany] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [loadingCourses, setLoadingCourses] = useState(true)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [loadError, setLoadError] = useState('')

  const selectedCourse = useMemo(() => courses.find((c) => c.slug === courseSlug), [courses, courseSlug])
  const selectedSession = useMemo(() => sessions.find((s) => s.id === sessionId), [sessions, sessionId])
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const canContinue = [
    Boolean(courseSlug),
    Boolean(sessionId),
    firstName.trim().length > 1 &&
      lastName.trim().length > 1 &&
      emailRegex.test(email.trim()) &&
      phone.trim().length > 6,
  ]

  // Load courses on mount
  useEffect(() => {
    let mounted = true
    fetchPublicCourses()
      .then((data) => {
        if (mounted) {
          setCourses(data.filter((c) => c.isActive !== false))
          setLoadingCourses(false)
        }
      })
      .catch((err) => {
        if (mounted) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load courses')
          setLoadingCourses(false)
        }
      })
    return () => { mounted = false }
  }, [])

  // Load sessions when course changes
  useEffect(() => {
    if (!courseSlug) {
      setSessions([])
      setSessionId('')
      return
    }
    let mounted = true
    setLoadingSessions(true)
    setSessionId('')
    fetchPublicSessions(courseSlug)
      .then((data) => {
        if (mounted) {
          setSessions(data)
          setLoadingSessions(false)
        }
      })
      .catch((err) => {
        if (mounted) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load sessions')
          setLoadingSessions(false)
        }
      })
    return () => { mounted = false }
  }, [courseSlug])

  async function submitBooking() {
    setSubmitError('')
    setIsSubmitting(true)
    try {
      if (!selectedCourse || !selectedSession) {throw new Error('Please select a course and date')}
      const res = await createCheckoutSession({
        courseSlug: selectedCourse.slug,
        sessionId: selectedSession.id,
        quantity,
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        company: company.trim(),
      })
      // Validate checkout URL before redirecting (prevent open redirect)
      const checkoutUrl = res.checkoutUrl
      if (!isValidCheckoutUrl(checkoutUrl)) {
        throw new Error('Invalid checkout URL received from server')
      }
      window.location.href = checkoutUrl
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not start checkout. Try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  function getB2cPrice(course: CourseFromAPI): number | null {
    const pricings = course.pricings ?? []
    for (const p of pricings) {
      if (p.priceType === 'b2c' && p.isActive !== false) {
        if (typeof p.price === 'number') {
          return p.price
        }
        const parsed = parseFloat(p.price as unknown as string)
        return Number.isNaN(parsed) ? null : parsed
      }
    }
    return null
  }

  function getOccupancyPct(session: PublicSession): number {
    const max = session.maxAttendees ?? 0
    if (!max) {return 0}
    const taken = max - session.spotsAvailable
    return Math.min(Math.round((taken / max) * 100), 100)
  }

  function getOccupancyColor(session: PublicSession): string {
    const pct = getOccupancyPct(session)
    if (pct >= 100) {return 'text-red-500'}
    if (pct >= 80) {return 'text-orange-500'}
    return 'text-green-600'
  }

  function formatDuration(min: number): string {
    if (min >= 480) {return `${min / 60}h`}
    return `${min} min`
  }

  if (loadError && !loadingCourses) {
    return (
      <div className={cn('bg-slate-950 text-stone-100 flex flex-col items-center justify-center', embedded ? 'w-full py-12' : 'min-h-screen')}>
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-red-300 font-medium mb-1">Error loading data</p>
        <p className="text-white/50 text-sm mb-4">{loadError}</p>
        <Button onClick={() => { setLoadError(''); setLoadingCourses(true); fetchPublicCourses().then(setCourses).catch(() => {}).finally(() => setLoadingCourses(false)) }}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('bg-slate-950 text-stone-100', embedded ? 'w-full' : 'min-h-screen')}>
      {!embedded && (
        <div className="border-b border-white/10 bg-dark-navy px-5 py-4">
          <div className="mx-auto flex w-full max-w-2xl items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-gold to-gold-dark">
                <ShieldCheck className="h-5 w-5 text-slate-950" />
              </div>
              <span className="text-sm font-semibold tracking-wide">UBSafety</span>
            </div>
            <span className="text-xs text-amber-300">Book class</span>
          </div>
        </div>
      )}

      <div className={cn('mx-auto w-full max-w-2xl', embedded ? 'p-3 md:p-4' : 'p-4 md:p-8')}>
        {step < 3 && (
          <div className="mb-6 flex items-center gap-2">
            {[0, 1, 2].map((i) => (
              <div key={`step-${i}`} className={cn('h-2 flex-1 rounded-full', i <= step ? 'bg-amber-300' : 'bg-white/10')} />
            ))}
          </div>
        )}

        {/* Step 0: Choose course */}
        {step === 0 && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Choose your training</h1>
            <p className="text-sm text-white/60">Select a course to see available dates and pricing.</p>

            {loadingCourses ? (
              <div className="flex items-center justify-center py-12 gap-3">
                <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />
                <span className="text-sm text-white/60">Loading courses…</span>
              </div>
            ) : courses.length === 0 ? (
              <div className="text-center py-12 text-white/50 text-sm">
                No courses available at the moment.
              </div>
            ) : (
              <div className="grid gap-2">
                {courses.map((c) => {
                  const price = getB2cPrice(c)
                  return (
                    <button
                      key={c.slug}
                      onClick={() => setCourseSlug(c.slug)}
                      className={cn(
                        'rounded-xl border p-4 text-left transition-colors',
                        courseSlug === c.slug
                          ? 'border-gold bg-amber-500/10'
                          : 'border-white/10 bg-gray-900 hover:border-gold/40'
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{c.name}</span>
                        <span className="font-semibold text-amber-300">
                          {price !== null ? `$${price.toFixed(2)}` : 'Contact us'}
                        </span>
                      </div>
                      <p className="text-xs text-white/50">{c.description || `Valid for ${c.validYears} year${c.validYears !== 1 ? 's' : ''}`}</p>
                    </button>
                  )
                })}
              </div>
            )}
            <Button
              className="w-full bg-gradient-to-r from-gold to-gold-dark text-slate-950"
              disabled={!canContinue[0] || loadingCourses}
              onClick={() => setStep(1)}
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 1: Select date/session */}
        {step === 1 && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Select a date</h1>
            <p className="text-sm text-white/60">
              Available sessions for <span className="text-amber-300 font-medium">{selectedCourse?.name}</span>
            </p>

            {loadingSessions ? (
              <div className="flex items-center justify-center py-12 gap-3">
                <Loader2 className="w-5 h-5 text-amber-300 animate-spin" />
                <span className="text-sm text-white/60">Loading sessions…</span>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-12 bg-gray-900 rounded-xl border border-white/10">
                <CalendarDays className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/60 text-sm font-medium mb-1">No sessions available</p>
                <p className="text-white/40 text-xs">There are no upcoming sessions for this course. Please check back later or contact us.</p>
              </div>
            ) : (
              <div className="grid gap-2">
                {sessions.map((s) => {
                  const full = s.spotsAvailable <= 0
                  const pct = getOccupancyPct(s)
                  return (
                    <button
                      key={s.id}
                      onClick={() => !full && setSessionId(s.id)}
                      disabled={full}
                      className={cn(
                        'rounded-xl border p-4 text-left transition-colors relative',
                        full
                          ? 'border-white/5 bg-slate-900 opacity-50 cursor-not-allowed'
                          : sessionId === s.id
                            ? 'border-gold bg-amber-500/10'
                            : 'border-white/10 bg-gray-900 hover:border-gold/40'
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-amber-300" />
                          {fmtDate(s.date, { weekday: 'long', month: 'short', day: 'numeric' })}
                        </span>
                        {full ? (
                          <span className="text-xs font-semibold text-red-400 bg-red-400/10 px-2 py-0.5 rounded">FULL</span>
                        ) : (
                          <span className={cn('text-xs font-semibold px-2 py-0.5 rounded', getOccupancyColor(s))}>
                            {s.spotsAvailable} spot{s.spotsAvailable !== 1 ? 's' : ''} left
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-white/50">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(s.durationMinutes)}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.locationName}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {s.instructorName}</span>
                      </div>
                      {/* Occupancy bar */}
                      <div className="mt-2 h-1 w-full bg-white/10 rounded-full overflow-hidden">
                        <div
                          className={cn('h-full rounded-full', pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-500' : 'bg-green-500')}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-gold to-gold-dark text-slate-950"
                disabled={!canContinue[1] || loadingSessions || sessions.length === 0}
                onClick={() => setStep(2)}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Student data */}
        {step === 2 && (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Your details</h1>
            <div className="bg-gray-900 rounded-xl border border-white/10 p-3 mb-2">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-1">Booking summary</p>
              <p className="text-sm font-medium">{selectedCourse?.name}</p>
              <p className="text-xs text-white/50">
                {selectedSession && fmtDate(selectedSession.date, { weekday: 'long', month: 'short', day: 'numeric' })} · {selectedSession?.locationName}
              </p>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none placeholder:text-white/30 focus:border-gold" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <input className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none placeholder:text-white/30 focus:border-gold" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <input className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none placeholder:text-white/30 focus:border-gold" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <input className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none placeholder:text-white/30 focus:border-gold" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              <input className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none placeholder:text-white/30 focus:border-gold" placeholder="Company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} />
              <div>
                <label className="block text-xs text-white/50 mb-1">Number of participants</label>
                <select className="h-10 w-full rounded-md border border-white/15 bg-slate-900 px-3 text-sm text-stone-100 outline-none focus:border-gold" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))}>
                  {Array.from({ length: Math.min(selectedSession?.spotsAvailable ?? 10, 10) }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n} participant{n !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            {submitError && <p className="text-sm text-red-300">{submitError}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-gold to-gold-dark text-slate-950"
                disabled={!canContinue[2] || isSubmitting}
                onClick={submitBooking}
              >
                {isSubmitting ? 'Redirecting to payment…' : 'Proceed to Payment'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Success (redirecting to Stripe, rarely shown) */}
        {step === 3 && (
          <div className="space-y-4 py-8 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-amber-300" />
            <h1 className="text-2xl font-bold">Redirecting…</h1>
            <p className="text-white/70">You are being redirected to our secure payment page.</p>
          </div>
        )}
      </div>
    </div>
  )
}
